import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useBreadcrumbs } from '../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { RoutePath } from '../../layout/Routes'
import { InsightsAiResults } from '../InsightsAiQuestionBox/InsightsAiResults'

import { InsightsAiDebugExport } from './InsightsAiDebugExport'
import { INSIGHTS_CHAT_BASE_PATH, insightsChatFetch } from '@percent/partner-dashboard/services/insightsAi/insightsChat'
import { AiChat, useAiChat } from '@percent/domain/ai-chat'
import { POSTHOG_FEATURE_FLAGS, usePostHogFeatureFlag } from '@percent/partner-dashboard/common/hooks'

/**
 * The "Ask about your impact" chat page: the generic ai-chat Conversation, bound to
 * the insights endpoint + metric renderer + copy, auto-sending the router-state question.
 */
export function AskImpactPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const initialQuestion = (location.state as { initialQuestion?: string } | null)?.initialQuestion
  const debugExportEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_INSIGHTS_AI_DEBUG_EXPORT)
  const chat = useAiChat({
    basePath: INSIGHTS_CHAT_BASE_PATH,
    fetch: insightsChatFetch,
    // Map the actionable ai-chat platform errors; the rest get a generic message.
    errorCopy: {
      'ai_chat/message_limit_reached': t('typography.dashboard.insightsAi.errors.messageLimit'),
      'ai_chat/turn_timeout': t('typography.dashboard.insightsAi.errors.timeout')
    },
    defaultErrorMessage: t('typography.dashboard.insightsAi.errors.generic')
  })
  const initialSentRef = useRef(false)

  useBreadcrumbs([
    { path: RoutePath.DASHBOARD, label: t('breadcrumb.dashboard') },
    { label: t('typography.dashboard.insightsAi.pageTitle') }
  ])

  // Auto-send the question the dashboard opened the page with, exactly once.
  useEffect(() => {
    if (initialSentRef.current || !initialQuestion) return
    initialSentRef.current = true
    chat.ask(initialQuestion)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuestion])

  return (
    <AiChat.Conversation
      chat={chat}
      renderData={data => <InsightsAiResults data={data} />}
      toolbar={debugExportEnabled ? <InsightsAiDebugExport chat={chat} /> : undefined}
      thinkingMessages={[
        t('typography.dashboard.insightsAi.thinking.metrics'),
        t('typography.dashboard.insightsAi.thinking.summarizing'),
        t('typography.dashboard.insightsAi.thinking.almost')
      ]}
      workingLabel={t('typography.dashboard.insightsAi.working')}
      logAriaLabel={t('typography.dashboard.insightsAi.pageTitle')}
      placeholder={t('typography.dashboard.insightsAi.inputPlaceholder')}
      submitLabel={t('typography.dashboard.insightsAi.ask')}
      betaLabel={t('typography.dashboard.beta')}
      inputName="insights-ai-question"
      testIds={{
        page: 'ask-impact-page',
        toolbar: 'ask-impact-toolbar',
        userMessage: 'ask-impact-user-message',
        thinking: 'ask-impact-thinking',
        error: 'ask-impact-error',
        answer: 'insights-ai-answer',
        input: 'insights-ai-input',
        submit: 'insights-ai-submit'
      }}
    />
  )
}
